import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import PropTypes from 'prop-types';
import {
  FormGroup,
  Label,
  LabelText,
  Input,
  themeUtils,
  InputMessage,
  InputAddonText,
  InputAddonNumberControls,
  useUpDownNumberControl,
} from '@zen-common/components-base';
import { numberUtils } from '@zen/common-utils';
import { NamingAssetStore, NamingUtils } from '@zen/common-app-parts';
import { DexSearchStore } from '@zen-dex/utils';
import Available from '../../../../../../components/Available';

/**
 * Renders the price-quantity-total form
 * Should get props from usePriceQuantityTotal hook
 */
export default function PriceQuantityTotalForm({
  price,
  quantity,
  total,
  limitError,
  onPriceChange,
  onQuantityChange,
  onTotalChange,
  validation,
  priceReadOnly,
  upToQuantity,
  forceReadOnly,
  available,
  isBuy,
}) {
  const { state: naming } = NamingAssetStore.useStore();
  const { state: searchState } = DexSearchStore.useStore();
  const { base, quote } = searchState;

  const priceKeyHandler = useUpDownNumberControl({
    min: 0,
    value: price.safeValue,
    onChange: onPriceChange,
  });
  const quantityKeyHandler = useUpDownNumberControl({
    min: 0,
    value: quantity.safeValue,
    onChange: onQuantityChange,
  });
  const totalKeyHandler = useUpDownNumberControl({
    min: 0,
    value: total.safeValue,
    onChange: onTotalChange,
  });

  if (forceReadOnly) {
    price.display = '';
    validation.price = '';
    quantity.display = '';
    validation.quantity = '';
    total.display = '';
    validation.total = '';
  }

  return (
    <>
      <Available base={base} quote={quote} available={available} isBuy={isBuy} />
      <FormGroup>
        <Label>
          <LabelText>Price per unit</LabelText>
          <Input
            value={price.display}
            onChange={(e) => onPriceChange(e.target.value.trim())}
            readOnly={forceReadOnly || priceReadOnly}
            variant="block"
            valid={validation.price ? false : undefined}
            renderRightAddon={
              !priceReadOnly && !forceReadOnly ? (
                <InputAddonNumberControls
                  onChange={onPriceChange}
                  value={price.safeValue}
                  min="0"
                />
              ) : null
            }
            {...priceKeyHandler}
          />
        </Label>
        <InputMessage>{validation.price}</InputMessage>
      </FormGroup>
      <FormGroup>
        <Label>
          <LabelText>Quantity {base && `- ${NamingUtils.displayName(base, naming)}`}</LabelText>
          <StyledQuantityInput
            readOnly={forceReadOnly}
            value={quantity.display}
            onChange={(e) => onQuantityChange(e.target.value.trim())}
            variant="block"
            valid={validation.quantity ? false : undefined}
            hasAddon={!!upToQuantity}
            renderRightAddon={
              <>
                {forceReadOnly ? null : (
                  <>
                    {upToQuantity ? (
                      <QuantityLimitAddon error={limitError}>
                        / {numberUtils.toDisplay(upToQuantity)}
                      </QuantityLimitAddon>
                    ) : null}
                    <InputAddonNumberControls
                      onChange={onQuantityChange}
                      value={quantity.safeValue}
                      min="0"
                      max={upToQuantity ? upToQuantity : undefined}
                    />
                  </>
                )}
              </>
            }
            {...quantityKeyHandler}
          />
        </Label>
        <InputMessage>{validation.quantity}</InputMessage>
      </FormGroup>
      <FormGroup>
        <Label>
          <LabelText>
            Total price {quote && `- ${NamingUtils.displayName(quote, naming)}`}
          </LabelText>
          <Input
            readOnly={forceReadOnly}
            value={total.display}
            onChange={(e) => onTotalChange(e.target.value.trim())}
            variant="block"
            valid={validation.total ? false : undefined}
            renderRightAddon={
              <>
                {forceReadOnly ? null : (
                  <InputAddonNumberControls
                    onChange={onTotalChange}
                    value={total.safeValue}
                    min="0"
                  />
                )}
              </>
            }
            {...totalKeyHandler}
          />
        </Label>
        <InputMessage>{validation.total}</InputMessage>
      </FormGroup>
    </>
  );
}
PriceQuantityTotalForm.propTypes = {
  price: PropTypes.object,
  quantity: PropTypes.object,
  total: PropTypes.object,
  limitError: PropTypes.any,
  onPriceChange: PropTypes.func,
  onQuantityChange: PropTypes.func,
  onTotalChange: PropTypes.func,
  validation: PropTypes.object,
  priceReadOnly: PropTypes.bool,
  upToQuantity: PropTypes.string,
};

const StyledQuantityInput = styled(Input)`
  ${(p) =>
    p.hasAddon
      ? css`
          input {
            padding-right: 0;
          }
        `
      : null}
`;

/**
 * Get an animation for the quantity error
 */
const getErrorAnimation = (p) => {
  const fontSizeNormal = themeUtils.fontSize(0)(p);
  const fontSizeBig = themeUtils.fontSize(2)(p);
  const danger = themeUtils.color('danger')(p);
  return keyframes`
    0%   {color: inherit; font-size: ${fontSizeNormal};}
    8%  {color: ${danger}; font-size: ${fontSizeBig};}
    90%  {color: ${danger}; font-size: ${fontSizeBig};}
    100% {color: inherit; font-size: ${fontSizeNormal};}
  `;
};
const QuantityLimitAddon = styled(InputAddonText)`
  ${(p) =>
    p.error
      ? css`
          animation: ${getErrorAnimation(p)} 2s forwards ease;
        `
      : ''}
`;
